import { TrendCurve, type TrendCurveSpec, type TrendLine } from '@/components/charts/TrendCurve';
import { Waterfall } from '@/components/lighthouse/Waterfall';
import { useLighthouseReport } from '@/components/lighthouse/use-lhr';
import { type NetworkRequest, mapNetworkRequests } from '@/components/lighthouse/waterfall-data';
import type { CondensedBarsDatum } from '@/components/primitives/CondensedBars';
import { ErrorState } from '@/components/primitives/ErrorState';
import { MetricTile } from '@/components/primitives/MetricTile';
import { PageActions } from '@/components/primitives/PageActions';
import { Island } from '@/components/shell/Island';
import { SiteSwitcherCrumb } from '@/components/shell/SiteSwitcherCrumb';
import { Button } from '@/components/ui/button';
import { useActiveSite } from '@/lib/active-site';
import type { Variant, VariantArmStats, VariantResults, VariantRun } from '@/lib/api-types';
import { toastSuccess } from '@/lib/errors';
import {
  type MetricBand,
  bandText,
  clsBand,
  fcpBand,
  lcpBand,
  scoreBand,
  tbtBand,
  ttfbBand,
} from '@/lib/format';
import { useSite } from '@/lib/queries';
import {
  useActivateVariant,
  useArchiveVariant,
  useBlockPartnerCatalog,
  useRunVariant,
  useVariant,
  useVariantResults,
  useVariantRuns,
} from '@/lib/queries/variants';
import { type VariantDeltas, resolveVariantDeltas } from '@clickweave/shared';
import { ArrowUpRight, CaretDown, CaretRight, GearSix, Play } from '@phosphor-icons/react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { type ReactElement, useEffect, useState } from 'react';

export const Route = createFileRoute('/_app/performance/variants/$variantUuid')({
  component: VariantDetailPage,
});

// ─── Formatting ──────────────────────────────────────────────────────

function fmtMs(v: number | null): string {
  if (v == null) return '—';
  return v >= 1000 ? `${(v / 1000).toFixed(2)}s` : `${Math.round(v)}ms`;
}

function fmtScore(v: number | null): string {
  return v == null ? '—' : String(Math.round(v * 100));
}

function fmtCls(v: number | null): string {
  if (v == null) return '—';
  return v.toFixed(v < 0.01 && v > 0 ? 3 : 2);
}

function fmtBytes(v: number | null): string {
  if (v == null) return '—';
  return v >= 1_048_576 ? `${(v / 1_048_576).toFixed(1)} MB` : `${Math.round(v / 1024)} KB`;
}

function fmtCount(v: number | null): string {
  return v == null ? '—' : String(Math.round(v));
}

interface MetricRowDef {
  key: keyof Omit<VariantArmStats, 'samples'>;
  label: string;
  fmt: (v: number | null) => string;
  /** Delta magnitude formatter (absolute value). */
  deltaFmt: (v: number) => string;
  lowerIsBetter: boolean;
  /** CWV/Lighthouse band for tinting the variant value; omit for unbanded metrics. */
  band?: (v: number | null) => MetricBand;
}

const METRIC_ROWS: MetricRowDef[] = [
  {
    key: 'performance_score',
    label: 'Performance',
    fmt: fmtScore,
    deltaFmt: (v) => `${Math.round(v * 100)} pts`,
    lowerIsBetter: false,
    band: (v) => scoreBand(v == null ? null : v * 100),
  },
  {
    key: 'lcp',
    label: 'LCP',
    fmt: fmtMs,
    deltaFmt: (v) => fmtMs(v),
    lowerIsBetter: true,
    band: lcpBand,
  },
  {
    key: 'fcp',
    label: 'FCP',
    fmt: fmtMs,
    deltaFmt: (v) => fmtMs(v),
    lowerIsBetter: true,
    band: fcpBand,
  },
  {
    key: 'ttfb',
    label: 'TTFB',
    fmt: fmtMs,
    deltaFmt: (v) => fmtMs(v),
    lowerIsBetter: true,
    band: ttfbBand,
  },
  {
    key: 'tbt',
    label: 'TBT',
    fmt: fmtMs,
    deltaFmt: (v) => fmtMs(v),
    lowerIsBetter: true,
    band: tbtBand,
  },
  {
    key: 'cls',
    label: 'CLS',
    fmt: fmtCls,
    deltaFmt: (v) => fmtCls(v),
    lowerIsBetter: true,
    band: clsBand,
  },
  {
    key: 'third_party_bytes',
    label: 'Third-party bytes',
    fmt: fmtBytes,
    deltaFmt: (v) => fmtBytes(v),
    lowerIsBetter: true,
  },
  {
    key: 'request_count',
    label: 'Requests',
    fmt: fmtCount,
    deltaFmt: (v) => fmtCount(v),
    lowerIsBetter: true,
  },
];

/** Improvement-coloured delta pill (never raw-sign coloured — §9.8). */
function DeltaPill({
  baseline,
  treatment,
  row,
}: {
  baseline: number | null;
  treatment: number | null;
  row: MetricRowDef;
}): ReactElement {
  if (baseline == null || treatment == null) {
    return <span className="text-xs text-[var(--color-mute)]">—</span>;
  }
  const diff = treatment - baseline;
  const pct = baseline !== 0 ? Math.round((diff / baseline) * 100) : null;
  const negligible = diff === 0 || (pct !== null && Math.abs(pct) < 1);
  const improved = row.lowerIsBetter ? diff < 0 : diff > 0;
  const tone = negligible
    ? 'bg-[var(--color-paper-alt)] text-[var(--color-mute)]'
    : improved
      ? 'bg-[var(--color-success)]/10 text-[var(--color-success)]'
      : 'bg-[var(--color-danger)]/10 text-[var(--color-danger)]';
  const sign = diff > 0 ? '+' : diff < 0 ? '−' : '±';
  const label = negligible
    ? '±0'
    : `${sign}${row.deltaFmt(Math.abs(diff))}${pct !== null ? ` (${sign}${Math.abs(pct)}%)` : ''}`;
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ${tone}`}
    >
      {label}
    </span>
  );
}

// ─── Sections ────────────────────────────────────────────────────────

function Chip({
  children,
  mono = false,
  title,
}: {
  children: string;
  mono?: boolean;
  title?: string;
}): ReactElement {
  return (
    <span
      title={title}
      className={`rounded-full bg-[var(--color-paper-alt)] px-2 py-0.5 text-xs text-[var(--color-ink-soft)] ${mono ? 'font-mono' : ''}`}
    >
      {children}
    </span>
  );
}

function fmtWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Status + window + how-it-runs context strip. */
function StatusStrip({
  variant,
  schedule,
  nextRunAt,
  pausedAt,
}: {
  variant: Variant;
  schedule: string | null;
  nextRunAt: string | null;
  pausedAt: string | null;
}): ReactElement {
  const window = variant.ends_at ? `fixed window · ends ${fmtWhen(variant.ends_at)}` : 'open-ended';
  const cadence =
    variant.status !== 'active'
      ? null
      : !schedule
        ? null // the no-schedule warning card below carries this case
        : pausedAt
          ? 'site schedule is paused'
          : nextRunAt
            ? `runs with the site's schedule · next ~ ${fmtWhen(nextRunAt)}`
            : "runs with the site's schedule";
  return (
    <section className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)] px-4 py-3">
      <span className="text-sm capitalize text-[var(--color-ink)]">{variant.status}</span>
      <span className="text-xs text-[var(--color-mute)]">{window}</span>
      {cadence && <span className="text-xs text-[var(--color-mute)]">{cadence}</span>}
      {variant.status === 'archived' && variant.archived_reason && (
        <span className="text-xs text-[var(--color-mute)]">
          archived · {variant.archived_reason === 'window' ? 'window elapsed' : 'manual'}
        </span>
      )}
    </section>
  );
}

/** Amber nudge when an active variant can never run on its own. */
function NoScheduleWarning(): ReactElement {
  return (
    <section className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/5 px-4 py-3">
      <p className="text-sm text-[var(--color-ink-soft)]">
        This site has no audit schedule, so this variant only runs when you run it manually.
      </p>
      <Link
        to="/settings/sites"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--color-ink)] hover:underline"
      >
        <GearSix aria-hidden size={16} />
        Configure schedule
      </Link>
    </section>
  );
}

/** No treatment data yet — explain the loop and offer the shortcut. */
function CollectingCard({
  variant,
  onRun,
  runPending,
}: {
  variant: Variant;
  onRun: () => void;
  runPending: boolean;
}): ReactElement {
  return (
    <section className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-[var(--color-line)] bg-[var(--color-paper-alt)] px-6 py-10 text-center">
      <Play aria-hidden size={28} className="text-[var(--color-mute)]" />
      <div className="space-y-1">
        <p className="text-sm font-medium text-[var(--color-ink)]">
          {variant.status === 'draft' ? 'No data yet' : 'Collecting…'}
        </p>
        <p className="mx-auto max-w-md text-xs text-[var(--color-mute)]">
          {variant.status === 'draft'
            ? 'Run a one-off comparison to see this variant against your baseline, or activate it to run on every scheduled audit.'
            : 'Treatment runs land alongside your scheduled audits. Run a comparison now to get your first numbers without waiting for the next tick.'}
        </p>
      </div>
      <Button variant="outline" disabled={runPending} onClick={onRun}>
        <Play aria-hidden />
        {runPending ? 'Dispatching…' : 'Run comparison now'}
      </Button>
    </section>
  );
}

/**
 * Headline tiles — the four metrics most likely to move under a
 * variant, rendered with the same `MetricTile` the page report uses
 * (band-tinted hero value + per-run bar history) so variants speak
 * the dashboard's visual language. Bars are the VARIANT arm's per-run
 * medians, so a one-off blip reads differently from a consistent
 * shift; the delta pill compares against the baseline median.
 */
const HEADLINE_KEYS: Array<MetricRowDef['key']> = [
  'performance_score',
  'lcp',
  'tbt',
  'third_party_bytes',
];

function HeadlineCards({
  results,
  runs,
}: {
  results: VariantResults;
  runs: VariantRun[];
}): ReactElement {
  const { baseline, treatment } = results;
  // API returns newest-first; bar history reads left → right in time.
  const runsAsc = [...runs].reverse();
  return (
    <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {HEADLINE_KEYS.map((key) => {
        const row = METRIC_ROWS.find((r) => r.key === key)!;
        const b = baseline[key];
        const t = treatment[key];
        const deltaPct = b != null && t != null && b !== 0 ? ((t - b) / b) * 100 : null;
        const bars: CondensedBarsDatum[] = runsAsc.map((r) => ({
          x: Date.parse(r.created_at),
          y: r.treatment[key],
        }));
        return (
          <MetricTile
            key={key}
            label={row.label}
            value={row.fmt(t)}
            band={row.band ? row.band(t) : 'unknown'}
            bars={bars}
            deltaPct={deltaPct}
            {...(row.band ? { barBand: (y: number) => row.band!(y) } : {})}
            lowerIsBetter={row.lowerIsBetter}
            barTitle={(d) => `${fmtWhen(new Date(d.x).toISOString())} · ${row.fmt(d.y)}`}
          />
        );
      })}
    </section>
  );
}

/**
 * Variant-health banner — when the newest batch produced zero variant
 * successes, every delta on the page is stale and the honest headline
 * is "your variant isn't running", not a number. Terminal failures
 * only; a batch that's still pending isn't a problem yet.
 */
function LatestRunNotice({ runs }: { runs: VariantRun[] }): ReactElement | null {
  const latest = runs[0];
  if (!latest || latest.treatment.samples > 0) return null;
  const tAudits = latest.audits.filter((a) => a.arm === 'treatment');
  if (tAudits.length === 0) return null;
  const timeouts = tAudits.filter((a) => a.status === 'timeout').length;
  const errors = tAudits.filter((a) => a.status === 'error').length;
  if (timeouts + errors === 0) return null; // still in flight
  const what =
    timeouts > 0 && errors === 0
      ? `all ${timeouts} variant audits timed out`
      : errors > 0 && timeouts === 0
        ? `all ${errors} variant audits failed`
        : `${timeouts} variant audits timed out and ${errors} failed`;
  return (
    <section className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/5 px-4 py-3">
      <p className="text-sm text-[var(--color-ink-soft)]">
        <span className="font-medium text-[var(--color-warning)]">Variant data isn't landing</span>{' '}
        — in the latest batch ({fmtWhen(latest.created_at)}) {what} while baseline audits succeeded.
        The medians below predate this.
      </p>
    </section>
  );
}

/** Baseline vs variant medians table — numbers + footnote, no verdict. */
function ResultsTable({ results }: { results: VariantResults }): ReactElement {
  const { baseline, treatment } = results;
  const rows = METRIC_ROWS.filter((row) => baseline[row.key] != null || treatment[row.key] != null);
  return (
    <section className="space-y-3 rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-xs font-medium uppercase tracking-[0.06em] text-[var(--color-mute)]">
          Baseline vs variant
        </h2>
        <span className="text-xs tabular-nums text-[var(--color-mute)]">
          {baseline.samples} baseline · {treatment.samples} variant runs
        </span>
      </div>
      {baseline.samples === 0 && (
        <p className="rounded-md border border-[var(--color-line-soft)] bg-[var(--color-paper-alt)]/60 px-3 py-2 text-xs text-[var(--color-mute)]">
          No baseline runs in this window yet — “Run comparison now” dispatches both arms together.
        </p>
      )}
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-[var(--color-mute)]">
            <th className="px-3 py-1.5 font-semibold uppercase tracking-[0.04em]">Metric</th>
            <th className="px-3 py-1.5 text-right font-semibold uppercase tracking-[0.04em]">
              Baseline
            </th>
            <th className="px-3 py-1.5 text-right font-semibold uppercase tracking-[0.04em]">
              Variant
            </th>
            <th className="px-3 py-1.5 text-right font-semibold uppercase tracking-[0.04em]">Δ</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className="border-b border-[var(--color-line-soft)]/70 last:border-0">
              <td className="px-3 py-1.5 text-[var(--color-ink-soft)]">{row.label}</td>
              <td className="px-3 py-1.5 text-right tabular-nums text-[var(--color-ink-soft)]">
                {row.fmt(baseline[row.key])}
              </td>
              <td
                className={`px-3 py-1.5 text-right font-semibold tabular-nums ${
                  row.band && treatment[row.key] != null
                    ? bandText(row.band(treatment[row.key]))
                    : 'text-[var(--color-ink)]'
                }`}
              >
                {row.fmt(treatment[row.key])}
              </td>
              <td className="px-3 py-1.5 text-right">
                <DeltaPill baseline={baseline[row.key]} treatment={treatment[row.key]} row={row} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-[var(--color-mute)]">
        Synthetic what-if · medians per arm · not real-user data.
      </p>
    </section>
  );
}

// ─── Baseline vs variant trend ───────────────────────────────────────
//
// Reuses the Performance page's `TrendCurve` renderer (threshold
// guides, hover tooltip, clickable points) so the two surfaces read as
// the same product. Two lines: baseline in the neutral desktop grey,
// variant in the brand accent. Each point is one comparison batch's
// cell-matched median; clicking a point opens that batch's
// representative audit (the arm's median-LCP run) — the same
// pip-to-audit affordance the audits-view chart has.

interface TrendMetricDef {
  key: MetricRowDef['key'];
  /** Pill label — short; the spec label carries the full name. */
  pill: string;
  spec: TrendCurveSpec;
  /** Adjust stored value to chart units (score 0–1 → 0–100). */
  scale?: (v: number) => number;
}

const TREND_METRICS: TrendMetricDef[] = [
  {
    key: 'lcp',
    pill: 'LCP',
    spec: {
      label: 'LCP',
      unit: 'ms',
      band: lcpBand,
      format: (v) => fmtMs(v ?? null),
      goodThreshold: 2500,
      poorThreshold: 4000,
    },
  },
  {
    key: 'fcp',
    pill: 'FCP',
    spec: {
      label: 'FCP',
      unit: 'ms',
      band: fcpBand,
      format: (v) => fmtMs(v ?? null),
      goodThreshold: 1800,
      poorThreshold: 3000,
    },
  },
  {
    key: 'ttfb',
    pill: 'TTFB',
    spec: {
      label: 'TTFB',
      unit: 'ms',
      band: ttfbBand,
      format: (v) => fmtMs(v ?? null),
      goodThreshold: 800,
      poorThreshold: 1800,
    },
  },
  {
    key: 'tbt',
    pill: 'TBT',
    spec: {
      label: 'TBT',
      unit: 'ms',
      band: tbtBand,
      format: (v) => fmtMs(v ?? null),
      goodThreshold: 200,
      poorThreshold: 600,
    },
  },
  {
    key: 'performance_score',
    pill: 'Score',
    scale: (v) => v * 100,
    spec: {
      label: 'Performance',
      unit: 'score',
      band: scoreBand,
      format: (v) => (v == null ? '—' : String(Math.round(v))),
      goodThreshold: 90,
      poorThreshold: 50,
    },
  },
  {
    key: 'third_party_bytes',
    pill: '3P bytes',
    spec: {
      label: 'Third-party bytes',
      unit: 'bytes',
      band: () => 'unknown',
      format: (v) => fmtBytes(v ?? null),
    },
  },
];

/** The arm's median-LCP successful audit — the batch's click-through target. */
function representativeAuditId(run: VariantRun, arm: 'baseline' | 'treatment'): string | undefined {
  const ok = run.audits
    .filter((a) => a.arm === arm && a.status === 'success' && a.lcp != null)
    .sort((a, b) => a.lcp! - b.lcp!);
  return ok[Math.floor(ok.length / 2)]?.audit_id;
}

const BASELINE_LINE_COLOR = '#94a3b8';

function VariantTrend({ runs }: { runs: VariantRun[] }): ReactElement {
  const [metricKey, setMetricKey] = useState<MetricRowDef['key']>('lcp');
  const navigate = useNavigate();
  const def = TREND_METRICS.find((m) => m.key === metricKey)!;
  // API returns newest-first; the x-axis reads left → right in time.
  const runsAsc = [...runs].reverse();
  const armPoints = (arm: 'baseline' | 'treatment'): TrendLine['points'] =>
    runsAsc.map((run) => {
      const raw = run[arm][def.key];
      const id = representativeAuditId(run, arm);
      return {
        // `day` is only ever displayed (TrendCurve's formatter passes
        // non-YYYY-MM-DD strings through verbatim), so a preformatted
        // batch label gives twice-daily batches distinct tooltips.
        day: fmtWhen(run.created_at),
        value: raw == null ? null : def.scale ? def.scale(raw) : raw,
        ...(id ? { id } : {}),
      };
    });
  const lines: TrendLine[] = [
    { key: 'baseline', color: BASELINE_LINE_COLOR, points: armPoints('baseline') },
    { key: 'variant', color: 'var(--color-accent-brand)', points: armPoints('treatment') },
  ];
  const hasData = lines.some((l) => l.points.some((p) => p.value != null));
  return (
    <section className="rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)]">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--color-line-soft)] px-4 py-3">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold text-[var(--color-ink)]">
            Baseline vs variant over time
          </h2>
          <span className="flex items-center gap-3 text-[11px] text-[var(--color-mute)]">
            <span className="flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className="inline-block h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: BASELINE_LINE_COLOR }}
              />
              baseline
            </span>
            <span className="flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--color-accent-brand)]"
              />
              variant
            </span>
          </span>
        </div>
        <div
          // biome-ignore lint/a11y/useSemanticElements: pill group
          role="group"
          aria-label="Metric"
          className="flex flex-wrap items-center gap-0.5 rounded-md border border-[var(--color-line-soft)] p-0.5"
        >
          {TREND_METRICS.map((m) => (
            <button
              key={m.key}
              type="button"
              aria-pressed={metricKey === m.key}
              onClick={() => setMetricKey(m.key)}
              className={`rounded px-1.5 py-0.5 text-[11px] font-medium transition-colors ${
                metricKey === m.key
                  ? 'bg-[var(--color-paper-alt)] text-[var(--color-ink)]'
                  : 'text-[var(--color-mute)] hover:text-[var(--color-ink-soft)]'
              }`}
            >
              {m.pill}
            </button>
          ))}
        </div>
      </header>
      <div className="px-4 py-5">
        {hasData ? (
          <TrendCurve
            lines={lines}
            spec={def.spec}
            deploys={[]}
            previousPoints={[]}
            onPointClick={(id) =>
              void navigate({ to: '/performance/audits/$auditId', params: { auditId: id } })
            }
          />
        ) : (
          <div className="flex h-[200px] items-center justify-center text-xs text-[var(--color-mute)]">
            No {def.spec.label} samples in these batches yet.
          </div>
        )}
        <p className="mt-3 text-xs text-[var(--color-mute)]">
          One point per comparison batch (cell-matched medians, last {runs.length}) — click a point
          to open that batch's representative audit. Gaps mean the arm produced no successful
          audits.
        </p>
      </div>
    </section>
  );
}

// ─── Side-by-side compare ────────────────────────────────────────────
//
// One representative run per arm (the batch's median-LCP success),
// rendered as paired waterfalls. Rows whose URL matches the variant's
// block-list are tinted — in the baseline those are the requests the
// variant removes; in the variant column any tinted row means blocking
// isn't applying. Collapsed by default: each column fetches a multi-MB
// Lighthouse report, so nothing loads until the user opens it.

/** Newest batch with a successful audit in BOTH arms — the compare target. */
function latestComparableRun(runs: VariantRun[]): VariantRun | undefined {
  return runs.find((r) => r.baseline.samples > 0 && r.treatment.samples > 0);
}

function SideBySideCompare({
  variant,
  runs,
}: {
  variant: Variant;
  runs: VariantRun[];
}): ReactElement | null {
  const [open, setOpen] = useState(false);
  const run = latestComparableRun(runs);
  const baselineId = run ? representativeAuditId(run, 'baseline') : undefined;
  const treatmentId = run ? representativeAuditId(run, 'treatment') : undefined;
  if (!run || !baselineId || !treatmentId) return null;
  // Same resolver the runner applies at dispatch, so the tinted rows
  // are exactly the requests the treatment blocks. Lighthouse patterns
  // are `*substring*` wildcards — strip the stars and substring-match.
  const substrings = resolveVariantDeltas(variant.deltas as VariantDeltas)
    .blockedUrlPatterns.map((p) => p.replaceAll('*', ''))
    .filter(Boolean);
  if (substrings.length === 0) return null;
  const match = (req: NetworkRequest): boolean => substrings.some((s) => req.url.includes(s));
  return (
    <section className="rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)]">
      <header className="px-4 py-3">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="flex w-full items-center gap-2 text-left outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-brand)]/40"
        >
          {open ? (
            <CaretDown aria-hidden size={14} className="text-[var(--color-mute)]" />
          ) : (
            <CaretRight aria-hidden size={14} className="text-[var(--color-mute)]" />
          )}
          <div>
            <h2 className="text-sm font-semibold text-[var(--color-ink)]">
              Side-by-side · median runs from {fmtWhen(run.created_at)}
            </h2>
            <p className="text-xs text-[var(--color-mute)]">
              Paired waterfalls with the variant's blocked-domain requests tinted — open to load
              both reports.
            </p>
          </div>
        </button>
      </header>
      {open && (
        <div className="grid gap-3 border-t border-[var(--color-line-soft)] p-3 xl:grid-cols-2">
          <CompareColumn arm="baseline" run={run} auditId={baselineId} match={match} />
          <CompareColumn arm="treatment" run={run} auditId={treatmentId} match={match} />
        </div>
      )}
    </section>
  );
}

function CompareColumn({
  arm,
  run,
  auditId,
  match,
}: {
  arm: 'baseline' | 'treatment';
  run: VariantRun;
  auditId: string;
  match: (req: NetworkRequest) => boolean;
}): ReactElement {
  const lhr = useLighthouseReport(auditId);
  const audit = run.audits.find((a) => a.audit_id === auditId);
  const hits = lhr.data ? mapNetworkRequests(lhr.data).filter(match).length : null;
  const isBaseline = arm === 'baseline';
  // Baseline: tinted rows are what the variant removes; none at all
  // means the block-list doesn't apply to this page. Variant: anything
  // tinted means a "blocked" request still went out.
  const verdict =
    hits == null
      ? null
      : isBaseline
        ? hits > 0
          ? {
              tone: 'text-[var(--color-ink-soft)]',
              label: `${hits} request${hits === 1 ? '' : 's'} to blocked domains — tinted below`,
            }
          : {
              tone: 'text-[var(--color-warning)]',
              label: 'no requests to blocked domains — the variant has nothing to remove here',
            }
        : hits === 0
          ? { tone: 'text-[var(--color-success)]', label: '0 blocked-domain requests' }
          : {
              tone: 'text-[var(--color-danger)]',
              label: `${hits} blocked-domain request${hits === 1 ? '' : 's'} still present — blocking may not be applying`,
            };
  return (
    <div className="min-w-0 space-y-2">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-1">
        <span className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--color-mute)]">
          {isBaseline ? 'Baseline' : 'Variant'}
        </span>
        <Link
          to="/performance/audits/$auditId"
          params={{ auditId }}
          className="group inline-flex items-center gap-0.5 font-mono text-xs text-[var(--color-ink-soft)] hover:text-[var(--color-ink)] hover:underline"
        >
          {auditId}
          <ArrowUpRight aria-hidden size={12} className="text-[var(--color-mute)]" />
        </Link>
        <span className={`text-xs font-semibold tabular-nums ${bandText(lcpBand(audit?.lcp))}`}>
          LCP {fmtMs(audit?.lcp ?? null)}
        </span>
        {verdict && <span className={`text-xs ${verdict.tone}`}>{verdict.label}</span>}
      </div>
      <Waterfall
        auditId={auditId}
        defaultExpanded
        highlight={{ match, title: "Targeted by this variant's block list" }}
      />
    </div>
  );
}

function DeltaSections({ variant }: { variant: Variant }): ReactElement {
  const catalog = useBlockPartnerCatalog();
  const selection = variant.deltas.blocked_partners;
  const cats = selection?.categories ?? [];
  const overrides = selection?.partner_overrides ?? {};
  const customDomains = selection?.custom_domains ?? [];
  // Resolve the stored selection to the concrete partners it blocks —
  // mirrors the server-side resolver so the page shows exactly what's
  // being picked up, not opaque category names.
  const catSet = new Set(cats);
  const partners = (catalog.data?.partners ?? []).filter((p) => {
    const override = overrides[p.key];
    if (override === 'block') return true;
    if (override === 'allow') return false;
    return catSet.has(p.category);
  });
  const hasSelection = cats.length > 0 || Object.keys(overrides).length > 0;
  const headerNames = Object.keys(variant.deltas.extra_headers ?? {});
  const urlQuery = variant.deltas.url_query;
  return (
    <>
      <section className="space-y-3 rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4">
        <h2 className="text-xs font-medium uppercase tracking-[0.06em] text-[var(--color-mute)]">
          Third parties removed
        </h2>
        {hasSelection && catalog.isLoading ? (
          <div aria-hidden="true" className="h-6 w-2/3 rounded-md bg-[var(--color-paper-alt)]/60" />
        ) : (
          <div className="flex flex-wrap gap-2">
            {partners.map((p) => (
              <Chip key={p.key} title={p.domains.join(', ')}>
                {p.name}
              </Chip>
            ))}
            {customDomains.map((d) => (
              <Chip key={d} mono>
                {d}
              </Chip>
            ))}
            {partners.length === 0 && customDomains.length === 0 && (
              <span className="text-xs text-[var(--color-mute)]">None.</span>
            )}
          </div>
        )}
        {partners.length > 0 && (
          <p className="text-xs text-[var(--color-mute)]">
            Requests to these third parties' domains are blocked during treatment runs — hover one
            for its domains.
          </p>
        )}
      </section>

      {headerNames.length > 0 && (
        <section className="space-y-3 rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4">
          <h2 className="text-xs font-medium uppercase tracking-[0.06em] text-[var(--color-mute)]">
            Headers sent
          </h2>
          <div className="flex flex-wrap gap-2">
            {headerNames.map((name) => (
              <Chip key={name} mono>
                {name}
              </Chip>
            ))}
          </div>
          <p className="text-xs text-[var(--color-mute)]">Values hidden — treated as sensitive.</p>
        </section>
      )}

      {urlQuery && (
        <section className="space-y-3 rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4">
          <h2 className="text-xs font-medium uppercase tracking-[0.06em] text-[var(--color-mute)]">
            URL params
          </h2>
          <Chip mono>{urlQuery}</Chip>
        </section>
      )}
    </>
  );
}

// ─── Page ────────────────────────────────────────────────────────────

function VariantDetailPage(): ReactElement {
  const { variantUuid } = Route.useParams();
  const variant = useVariant(variantUuid);
  const v = variant.data;

  // Keep the chrome's active site in sync with the variant on screen
  // (same convention as the audit-detail deep-link handling).
  const { setActiveSite } = useActiveSite();
  useEffect(() => {
    if (v?.site_uuid) setActiveSite(v.site_uuid);
  }, [v?.site_uuid, setActiveSite]);

  const site = useSite(v?.site_uuid);
  const results = useVariantResults(variantUuid, v?.status === 'active');
  const runs = useVariantRuns(variantUuid, v?.status === 'active');
  const activate = useActivateVariant();
  const archive = useArchiveVariant();
  const run = useRunVariant();

  const onRun = (): void => {
    run.mutate(
      { uuid: variantUuid },
      {
        onSuccess: (res) => {
          toastSuccess(
            'Comparison dispatched',
            `${res.count} audit${res.count === 1 ? '' : 's'} per arm — results land here as they finish.`,
          );
        },
      },
    );
  };

  const breadcrumb = [
    { label: site.data?.name ?? v?.site_uuid ?? '', render: <SiteSwitcherCrumb /> },
    { label: 'Performance', href: '/performance' },
    { label: 'Variants', href: '/performance/variants' },
    { label: v?.name ?? variantUuid },
  ];

  const audit = site.data?.audit;
  const hasTreatmentData = (results.data?.treatment.samples ?? 0) > 0;

  return (
    <Island
      breadcrumb={breadcrumb}
      actions={
        v && v.status !== 'archived' ? (
          <PageActions>
            <Button variant="outline" disabled={run.isPending} onClick={onRun}>
              <Play aria-hidden />
              {run.isPending ? 'Dispatching…' : 'Run comparison'}
            </Button>
            {v.status === 'draft' && (
              <Button
                disabled={activate.isPending}
                onClick={() => activate.mutate({ uuid: v.uuid })}
              >
                Activate
              </Button>
            )}
            {v.status === 'active' && (
              <Button
                variant="ghost"
                disabled={archive.isPending}
                onClick={() => archive.mutate({ uuid: v.uuid })}
              >
                Archive
              </Button>
            )}
          </PageActions>
        ) : undefined
      }
    >
      {variant.isError ? (
        <ErrorState
          title="Couldn't load this variant"
          error={variant.error}
          retry={() => {
            void variant.refetch();
          }}
        />
      ) : variant.isLoading || !v ? (
        <div className="space-y-6" aria-hidden="true">
          <div className="h-12 rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)]" />
          <div className="h-40 rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)]" />
          <div className="h-24 rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)]" />
        </div>
      ) : (
        <div className="space-y-6">
          <StatusStrip
            variant={v}
            schedule={audit?.schedule ?? null}
            nextRunAt={audit?.next_run_at ?? null}
            pausedAt={audit?.paused_at ?? null}
          />

          {v.status === 'active' && audit && !audit.schedule && <NoScheduleWarning />}

          <LatestRunNotice runs={runs.data?.runs ?? []} />

          {results.isLoading ? (
            <div
              aria-hidden="true"
              className="h-40 rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)]"
            />
          ) : hasTreatmentData && results.data ? (
            <>
              <HeadlineCards results={results.data} runs={runs.data?.runs ?? []} />
              <ResultsTable results={results.data} />
            </>
          ) : v.status === 'archived' ? (
            <section className="rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4">
              <p className="text-sm text-[var(--color-ink-soft)]">
                This variant was archived before any treatment runs landed.
              </p>
            </section>
          ) : (
            <CollectingCard variant={v} onRun={onRun} runPending={run.isPending} />
          )}

          {(runs.data?.runs.length ?? 0) > 0 && <VariantTrend runs={runs.data!.runs} />}

          {(runs.data?.runs.length ?? 0) > 0 && (
            <SideBySideCompare variant={v} runs={runs.data!.runs} />
          )}

          <DeltaSections variant={v} />
        </div>
      )}
    </Island>
  );
}
